import React, { FC, useEffect, useState, useRef } from "react";
import { useQuery, useLazyQuery } from "@apollo/client";
import { Skeleton } from "antd";
import { useExternalMattersQueryLazy } from "~/hooks/external-matters";
import { useMutation, useCurrentUserData } from "~/hooks";
import { UPDATE_MATTER, CREATE_MATTER } from "../../graphql/mutation";
import {
  ORGANISATIONAL_UNITS_QUERY,
  FIND_ACCRUED_FEES_FOR_USER,
  FIND_CUMULATIVE_ACCRUED_FEES_FOR_EXTERNAL_MATTER,
} from "../../graphql/query";
import {
  FindOrganisationalUnitsQuery,
  FindCumulativeAccruedFeesForExternalMatterQuery,
} from "~/gql/graphql";
import {
  ExternalMatterStatus,
  ExternalMatter,
  MatterStatus,
  PricingType,
  PricingLevel,
} from "@design-system";
import { PmsMatters } from "@design-system";

export type PmsMattersContainerProps = {
  pmsName: string;
};

export const PmsMattersContainer: FC<PmsMattersContainerProps> = ({ pmsName }) => {
  const externalMatterQuery = useExternalMattersQueryLazy();
  const { currentUser } = useCurrentUserData({ cache: false });
  const [search, setSearch] = useState<string | undefined>(undefined);
  const [pageSize, setPageSize] = useState(3);
  const tableContainerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tableHeight = tableContainerRef.current?.getBoundingClientRect().y;
    if (tableHeight) {
      setPageSize(Math.floor((tableHeight - 320) / 50));
    }
  }, []);

  const [updateMatter, { loading: updateMatterLoading }] = useMutation(UPDATE_MATTER, {
    onCompleted: () => {
      const offset = (page - 1) * pageSize;
      currentUser?.feeEarnerId &&
        externalMatterQuery.refetch({
          clientId: null,
          statuses: [ExternalMatterStatus.Unassigned],
          search,
          limit: pageSize,
          offset,
          leadFeeEarnerId: currentUser?.feeEarnerId ?? undefined,
        });
    },
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FIND_ACCRUED_FEES_FOR_USER,
        variables: {
          filter: {
            matterStatus: MatterStatus.Live,
          },
        },
      },
    ],
    update: (cache) => {
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "filteredExternalMatters",
      });
    },
  });
  const [createMatter] = useMutation(CREATE_MATTER, {
    awaitRefetchQueries: true,
    refetchQueries: [
      {
        query: FIND_ACCRUED_FEES_FOR_USER,
        variables: {
          filter: {
            matterStatus: MatterStatus.Live,
          },
        },
      },
    ],
    update: (cache) => {
      cache.evict({
        id: "ROOT_QUERY",
        fieldName: "filteredExternalMatters",
      });
    },
  });
  const { data: organisationalUnitData } = useQuery<FindOrganisationalUnitsQuery>(
    ORGANISATIONAL_UNITS_QUERY
  );
  const [getCumulativeAccruedFees, { data: cumulativeAccruedFeesData }] =
    useLazyQuery<FindCumulativeAccruedFeesForExternalMatterQuery>(
      FIND_CUMULATIVE_ACCRUED_FEES_FOR_EXTERNAL_MATTER
    );

  const getCumulativeAccruedFeesForExternalMatter = (externalMatterId: number) => {
    getCumulativeAccruedFees({
      variables: {
        externalMatterId,
      },
    });
  };

  useEffect(() => {
    if (search && search.trim() !== "") {
      setPage(1);
      const offset = 0;
      externalMatterQuery.refetch({
        clientId: null,
        statuses: [ExternalMatterStatus.Unassigned],
        search,
        limit: pageSize,
        offset,
        leadFeeEarnerId: currentUser?.feeEarnerId ?? undefined,
      });
    }
  }, [search]);

  const [page, setPage] = useState(1);
  useEffect(() => {
    if (!currentUser?.feeEarnerId) {
      return;
    }
    const offset = (page - 1) * pageSize;
    externalMatterQuery.get({
      clientId: null,
      statuses: [ExternalMatterStatus.Unassigned],
      search,
      limit: pageSize,
      offset,
      leadFeeEarnerId: currentUser?.feeEarnerId ?? undefined,
    });
  }, [page, pageSize, currentUser?.feeEarnerId]);

  const handleSearch = (newSearch: string | undefined) => {
    setSearch(newSearch);
    setPage(1);
    currentUser?.feeEarnerId &&
      externalMatterQuery.refetch({
        clientId: null,
        statuses: [ExternalMatterStatus.Unassigned],
        search: newSearch,
        limit: pageSize,
        offset: 0,
        leadFeeEarnerId: currentUser?.feeEarnerId ?? undefined,
      });
  };

  return (
    <div ref={tableContainerRef}>
      <PmsMatters
        externalMatterData={externalMatterQuery}
        loading={externalMatterQuery.loading || updateMatterLoading}
        practiceAreas={organisationalUnitData?.organisationalUnits ?? []}
        pagination={{
          total: externalMatterQuery.data?.total ?? 0,
          current: page,
          pageSize,
        }}
        setPage={setPage}
        onLinkExternalMatterToMatter={async (externalMatterId, matterId) => {
          updateMatter({
            update: (cache, newData) => {
              if (newData.data?.updateMatter.externalMatterId) {
                cache.modify({
                  id: `ExternalMatter:${newData.data?.updateMatter.externalMatterId}`,
                  fields: {
                    status() {
                      return (
                        newData.data?.updateMatter.externalMatter?.status ??
                        ExternalMatterStatus.Unassigned
                      );
                    },
                    matter() {
                      return {
                        id: matterId,
                      };
                    },
                  },
                });
              }
            },
            variables: {
              id: matterId,
              data: {
                externalMatter: { connect: { id: externalMatterId } },
                status: { set: MatterStatus.Live },
              },
            },
          });
        }}
        onCreateMatterFromPms={async (
          externalMatter: ExternalMatter,
          budget: number,
          organisationalUnitId: number
        ) => {
          createMatter({
            variables: {
              createFromOptions: null,
              data: {
                externalMatter: { connect: { id: externalMatter.id } },
                ...(externalMatter.organisationalUnit
                  ? {
                      organisationalUnit: {
                        connect: { id: externalMatter.organisationalUnit.id },
                      },
                    }
                  : {
                      organisationalUnit: { connect: { id: organisationalUnitId } },
                    }),
                ...(externalMatter.client && {
                  client: { connect: { id: externalMatter.client.id } },
                }),
                title: externalMatter.title,
                status: MatterStatus.Live,
                pricingType: PricingType.Fixed,
                pricingLevel: PricingLevel.Matter,
                priceOptions: {
                  create: {
                    fixedChosen: budget,
                  },
                },
                ...(externalMatter.currency && {
                  currency: { connect: { id: externalMatter.currency.id } },
                }),
                createdFromPms: true,
              },
              parentPhaseId: null,
              parentId: null,
            },
          });
        }}
        pmsName={pmsName}
        getCumulativeAccruedFeesForExternalMatter={getCumulativeAccruedFeesForExternalMatter}
        cumulativeAccruedFeesData={cumulativeAccruedFeesData}
        search={search}
        onSearch={handleSearch}
      />
    </div>
  );
};
